import { isDefined } from "@clipboard-health/util-ts";
import { useMemo } from "react";

import { CbhFeatureFlag } from "../../../FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "../../../FeatureFlags/useCbhFlag";
import { useWorkerGeoLocation } from "../../../Worker/useWorkerGeoLocation";
import { useGetOpenShiftsForDates } from "../../Shift/Open/useGetOpenShiftsForDates";
import { useGetShiftDiscoveryDefaultDateRange } from "../../utils/useGetShiftDiscoveryDefaultDateRange";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../Filters/constants";

interface UseFallbackShiftsParams {
  appliedFiltersCount: number;
  openShiftsCount: number;
  visibleShiftIds: Set<string>;
}

type FallbackShiftsQueryResult = ReturnType<typeof useGetOpenShiftsForDates>;

interface UseFallbackShiftsReturn {
  shouldFetchFallback: boolean;
  shouldShowFallbackShifts: boolean;
  /**
   * `fallbackShiftsData.shifts` is deduplicated against the visible list, so it only
   * contains shifts the user cannot currently see. Use `totalFallbackShiftsCount` for
   * the full count the user would see after clearing filters.
   */
  fallbackShiftsData: FallbackShiftsQueryResult;
  totalFallbackShiftsCount: number;
}

/**
 * Custom hook that manages fallback shifts logic when filters are applied but return no results.
 * Fetches unfiltered shifts as a fallback to show users there are shifts available outside their current filters.
 * Deduplicates against shifts already visible in the main list so the same shift never appears twice,
 * and suppresses the fallback section entirely when filters aren't actually hiding anything.
 */
export function useFallbackShifts({
  appliedFiltersCount,
  openShiftsCount,
  visibleShiftIds,
}: UseFallbackShiftsParams): UseFallbackShiftsReturn {
  const shouldFetchFallback = openShiftsCount < 15 && appliedFiltersCount > 0;

  const workerGeoLocation = useWorkerGeoLocation();
  const windowedOutShiftsEnabled = useCbhFlag(CbhFeatureFlag.INCLUDE_WINDOWED_OUT_SHIFTS, {
    defaultValue: false,
  });
  const defaultDateRange = useGetShiftDiscoveryDefaultDateRange();

  const includeWindowedOut = windowedOutShiftsEnabled;

  const rawFallbackShiftsData = useGetOpenShiftsForDates({
    dates: [],
    dateRange: defaultDateRange,
    filter: {
      area: {
        latitude: workerGeoLocation?.latitude ?? 0,
        longitude: workerGeoLocation?.longitude ?? 0,
        radiusInMiles: DEFAULT_DISTANCE_IN_MILES_FILTER,
      },
      includeWindowedOutShifts: includeWindowedOut,
    },
    enabled: shouldFetchFallback && isDefined(workerGeoLocation),
  });

  const dedupedFallbackShifts = useMemo(
    () => rawFallbackShiftsData.shifts.filter((shift) => !visibleShiftIds.has(shift.id)),
    [rawFallbackShiftsData.shifts, visibleShiftIds]
  );

  const fallbackShiftsData = useMemo(
    () => ({ ...rawFallbackShiftsData, shifts: dedupedFallbackShifts }),
    [rawFallbackShiftsData, dedupedFallbackShifts]
  );

  const shouldShowFallbackShifts =
    shouldFetchFallback && dedupedFallbackShifts.length > 0 && rawFallbackShiftsData.isSuccess;

  return {
    shouldFetchFallback,
    shouldShowFallbackShifts,
    fallbackShiftsData,
    totalFallbackShiftsCount: rawFallbackShiftsData.shifts.length,
  };
}
